import { useEffect } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { chatKeys } from '~/lib/query-keys';

/**
 * FRONT-3211: an open chat shows a turn the SERVER started in it (a chat
 * task's result turn; any queued server turn) live, like a normal reply.
 *
 * Such a turn is not this tab's own stream, and `use-sse.ts` deliberately
 * does not re-read history when a turn starts: its `background-chat-changed`
 * start event is published before the turn's streaming row exists, so a
 * re-read at that moment finds nothing to attach to (FRONT-1717). What the
 * tab does know is the server's view of the chat (`useBackgroundChatForSession`,
 * polled while running): a turn is running and no local stream is attached.
 * While that holds, this re-reads the chat's history on a short schedule.
 * As soon as a re-read brings back the turn's assistant row marked
 * `streamLive`, `useAgentChat`'s existing history effect seeds it and the
 * resume pursuit (FRONT-2195) attaches to its stream: the "Results: …" row,
 * then the reply streaming with its tool cards. Attached, the local status
 * is streaming, the condition is false and the schedule stops. Same idea as
 * the late-approval pickup (`late-continuation-pickup.ts`); nothing here
 * opens a stream or sends a turn.
 */

/** Offsets of the first re-reads after the server reports a running turn. */
export const SERVER_TURN_REREAD_DELAYS_MS: readonly number[] = [400, 1_200, 2_500, 5_000];
/** After those, while still unattached: one re-read this often. */
export const SERVER_TURN_REREAD_EVERY_MS = 10_000;

/**
 * Pure: a turn runs on the server and this tab shows no stream of it. A run
 * this tab streamed itself is not one to pick up: right after its own turn
 * ends, the polled row still says `running` for a moment, and re-reading
 * history then would only add a second writer at turn end (FRONT-1717).
 */
export function isServerTurnUnattached(
  bgRun: { status?: string; runId?: string } | null | undefined,
  localStatus: string,
  lastStreamedRunId: string | null,
): boolean {
  if (bgRun?.status !== 'running') return false;
  if (localStatus === 'streaming' || localStatus === 'submitted') return false;
  return !bgRun.runId || bgRun.runId !== lastStreamedRunId;
}

export interface ServerTurnPickupDeps {
  queryClient: Pick<QueryClient, 'invalidateQueries'>;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

/**
 * Start the re-read schedule for one chat; returns its cancel. Exported for
 * tests (the hook below is its only caller).
 */
export function scheduleServerTurnRereads(sessionId: string, deps: ServerTurnPickupDeps): () => void {
  const setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>));
  const reread = () => void deps.queryClient.invalidateQueries({ queryKey: [...chatKeys.all, 'history', sessionId] });
  const handles: unknown[] = [];
  let stopped = false;
  for (const ms of SERVER_TURN_REREAD_DELAYS_MS) handles.push(setTimer(() => !stopped && reread(), ms));
  const last = SERVER_TURN_REREAD_DELAYS_MS[SERVER_TURN_REREAD_DELAYS_MS.length - 1] ?? 0;
  const every = () => {
    if (stopped) return;
    reread();
    handles.push(setTimer(every, SERVER_TURN_REREAD_EVERY_MS));
  };
  handles.push(setTimer(every, last + SERVER_TURN_REREAD_EVERY_MS));
  return () => {
    stopped = true;
    for (const h of handles) clearTimer(h);
  };
}

/** Re-read history while the server runs a turn in this chat that this tab is not attached to. */
export function useServerTurnPickup(sessionId: string | undefined, unattached: boolean): void {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!sessionId || !unattached) return;
    return scheduleServerTurnRereads(sessionId, { queryClient });
  }, [sessionId, unattached, queryClient]);
}
